import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatRow, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { AddEditStreamComponent } from '../add-edit-stream/add-edit-stream.component';
import { FeedstockService } from '../../../services/feedstockservice';
import { DepackService } from '../../../services/depack.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IdName } from '../../../interfaces/pricematrixmodel';
import { ConfirmationDialogComponent, ConfirmationDialogData } from '../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DepackStreamGridResponse } from '../../../interfaces/feedstocklistmodel';
import { PriceMatrixService } from '../../../services/pricematrix.service';

@Component({
  selector: 'app-streams-grid',
  imports: [MatIcon, MatTableModule, MatRow, MatPaginatorModule, MatSortModule, CommonModule, MatButtonModule],
  templateUrl: './streams-grid.component.html',
  styleUrl: './streams-grid.component.scss'
})
export class StreamsGridComponent implements OnInit, OnChanges {
  @Input() generatorId: number | null = null;
  @Input() sid: string | null = null;
  @Input() hid: string | null = null;
  @Input() locName: string | null = null;
  @Input() fsLoadId!: number;
  @Input() dateReceived: string | null = null;
  @Input() actualDate: string = '';
  @Input() loadStreams: DepackStreamGridResponse[] = []
  private destroyRef = inject(DestroyRef);
  streamsResponse: DepackStreamGridResponse[] = []; 
  private tempStreamKey = 0;
  @Output() loadStreamsEvent = new EventEmitter<DepackStreamGridResponse[]>();

  constructor(private dialog: MatDialog,
    private depackService: DepackService,    
    private fsService: FeedstockService,) { }


  ngOnInit(): void {
    if (!this.loadStreams || this.loadStreams.length === 0) {
      this.getStreamsForDepack();
    }
  }

  displayedAdditionalStreamColumns: string[] = ['actions', 'streamName', 'uom', "quantity", 'tipFee', 'rebateTotal', 'total'];
  dataSource = new MatTableDataSource<any>([]);

  getStreamsForDepack() {
    if (this.fsLoadId > 0) {
      this.depackService.getDeepackStreamsByLoadId(this.fsLoadId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (res: DepackStreamGridResponse[]) => {
            console.log("Streams grid", res);
            if(res.length > 0){
              this.streamsResponse = res;
              this.dataSource.data = this.streamsResponse;

              //Emit the streams
              this.loadStreamsEvent.emit(this.streamsResponse);
            }
          }
        });
    }
  }
  
  getRebateTotal(): number {
    return this.dataSource.data.reduce(
      (sum, row) => sum + Number(row.rebateTotal ?? 0),
      0
    );
  }
  
  getGrandTotal(): number {
    return this.dataSource.data.reduce(
      (sum, row) => sum + Number(row.total ?? 0),
      0
    );
  }

  openAddEditStreamDialog(mode: string, rowId: number ,
    selectedStreamRecord:DepackStreamGridResponse | null): void {
    const dialogRef = this.dialog.open(AddEditStreamComponent, {
      disableClose: true,
      data: {
        mode: mode,
        generatorId: this.generatorId,
        locName: this.locName,
        sid: this.sid,
        hid: this.hid,
        actualDate: this.actualDate,
        fsLoadId: this.fsLoadId,
        streamRecord:selectedStreamRecord,
        existingStreams: this.dataSource.data,
      },
      width: '850px',
      maxWidth: '850px',
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const streamRecord = result?.streamRecord ?? result;
        if (!streamRecord) {
          return;
        }

        if (mode === 'Add') {
          streamRecord.tempKey = ++this.tempStreamKey;
          this.streamsResponse.push(streamRecord);
        } 
        else if (mode === 'Edit') {
          const index = this.streamsResponse.findIndex(x =>
            x.id && x.id > 0
              ? x.id === streamRecord.id
              : x.tempKey === streamRecord.tempKey
          );

          if (index > -1) {
            this.streamsResponse[index] = streamRecord;
          }
        }

        this.dataSource.data = [...this.streamsResponse];
        this.loadStreamsEvent.emit(this.streamsResponse);
      }
    });
  }

  deleteStream(row: DepackStreamGridResponse) {
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete Additional Stream ${row.streamName}?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };

    this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      panelClass: 'delete-confirmation-dialog',
      data: dialogData,
      disableClose: true,
    }).afterClosed().subscribe(result => {
      if (result) {
        if (!row.id || row.id === 0) {
          this.streamsResponse = this.streamsResponse.filter(x => x !== row);
          this.dataSource.data = [...this.streamsResponse];
          this.loadStreamsEvent.emit(this.streamsResponse);
          return;
        }
        if (row.id !== null && row.id !== undefined) {
          if (row.id > 0) {            
            this.dataSource.data = this.dataSource.data.filter(item => item.id !== row.id);
            this.fsService.deleteFSLoadAdditionalStreamsById(row.id).subscribe({
              next: async (response) => {
                if (response) {
                  this.getStreamsForDepack();
                }
              }
            });
          }
        }
      }
    });
  }

  isAddStreamDisabled(): boolean {
    if (this.fsLoadId > 0)
      return false;
    return !this.generatorId || !this.sid || !this.hid || !this.locName;
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loadStreams'] && this.loadStreams?.length > 0) {
      this.streamsResponse = [...this.loadStreams];
      this.dataSource.data = [...this.streamsResponse];
    }
  }

}
